import React, { useContext, useMemo, useState } from 'react';
import { Modal, ModalProps } from '@vimily/bonjoro-ui-components-v2/Modal';
import { useGlobalEvent } from 'beautiful-react-hooks';
import { Input } from '@vimily/bonjoro-ui-components-v2/Input';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import {
  RedeemCouponErrorArgs,
  queryKeys as couponQueryKeys,
  useRedeemCoupon,
} from '~/App/Api/Coupon';
import { useQueryClient } from 'react-query';
import FlashContext from '~/App/Components/Flash/Context';
import { queryKeys as subscriptionQueryKeys } from '../../Hooks/useSubscription';
import { queryKeys as checkoutQueryKeys } from '../../Hooks/useCheckout';
import {
  AnimatedDiv,
  ShowAnimation,
} from '@vimily/bonjoro-ui-components-v2/AnimatedDiv';
import useCurrentTeam from '~/react-hooks/use-current-team';
import { PaymentGateway } from '~/types/models';

const openRedeemCouponModalEventName = 'redeem-coupon-modal::open';

export const openRedeemCouponModal = () =>
  window.dispatchEvent(new CustomEvent(openRedeemCouponModalEventName));

export const RedeemCouponModal = () => {
  const { tap } = window;
  const [open, setOpen] = useState(false);
  const [coupon, setCoupon] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const queryClient = useQueryClient();
  const { createFlash } = useContext(FlashContext);
  const {
    payment_gateway : paymentGateway,
    payment_gateway_id: customerId,
  } = useCurrentTeam();

  const onOpenRedeemCouponModal = useGlobalEvent(
    openRedeemCouponModalEventName as keyof WindowEventMap
  );

  onOpenRedeemCouponModal(() => setOpen(true));

  const closeModal = () => {
    setOpen(false);
    setCoupon(undefined);
    setErrorMessage(undefined);
  };

  const { mutateAsync: redeemCouponMutation, isLoading: loading } =
    useRedeemCoupon();

  const onRedeemClick = () => {
    if (!coupon) {
      setErrorMessage('This field is required');
      return;
    }

    setErrorMessage(undefined);

    void redeemCouponMutation(
      { coupon },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries('redeem-coupon');
          queryClient.invalidateQueries(couponQueryKeys.all);
          queryClient.invalidateQueries(subscriptionQueryKeys.all);
          queryClient.invalidateQueries(checkoutQueryKeys.all);

          if(paymentGateway === PaymentGateway.Stripe) {
            tap('conversion', customerId, 0, {'coupons': coupon}, 'standard');
          }

          createFlash({
            id: 'redeem-coupon',
            title: 'Success',
            message: data.message,
          });

          closeModal();
        },
        onError: (error: RedeemCouponErrorArgs) => {
          setErrorMessage(error?.response?.data?.message);
        },
      }
    );
  };

  const modalProps = useMemo<ModalProps>(() => {
    return {
      open,
      onModalClose: closeModal,
      closeButtonClassName: '!w-5',
      className: '!w-[479px] !inset-px !p-7',
    };
  }, [open]);

  return (
    <Modal {...modalProps}>
      <div className='flex flex-col gap-y-5 w-full'>
        <span className='font-bold text-[21px] pb-2 v2-text-neutral-900'>
          Enter a coupon code
        </span>
        <div className='flex flex-col gap-y-1.5'>
          <Input
            className='px-5 v2-text-neutral-900'
            value={coupon}
            placeholder='Input your coupon code'
            disabled={loading}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCoupon(e.target.value)
            }
          />
          {errorMessage && (
            <AnimatedDiv
              show
              showAnimation={ShowAnimation.SlideInRight150}
              className='text-xs v2-text-red-900'
            >
              {errorMessage}
            </AnimatedDiv>
          )}
        </div>
        <Button
          variant={ButtonVariant.Primary}
          disabled={loading}
          onClick={onRedeemClick}
          className='py-2'
        >
          {loading && <span className='fa fa-spinner spinning mr-2' />}
          Redeem coupon
        </Button>
      </div>
    </Modal>
  );
};
